import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { LogIn, Settings, Mail, AlertTriangle, Sun, Moon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

export const LoginScreen = () => {
  const { login, loginError, setLoginError } = useAuth();
  const { darkMode, setDarkMode } = useTheme();
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [resetRequest, setResetRequest] = useState({ username: '', email: '' });

  const handleLogin = async () => {
    await login(username, password);
  };

  const handlePasswordResetRequest = () => {
    if (!resetRequest.username.trim() || !resetRequest.email.trim()) {
      alert('Пожалуйста, заполните все поля');
      return;
    }
    alert('Для восстановления пароля обратитесь к администратору системы.');
    setShowForgotPassword(false);
    setResetRequest({ username: '', email: '' });
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 to-indigo-100'} flex items-center justify-center p-4`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`rounded-2xl shadow-xl p-8 w-full max-w-md ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
      >
        <div className="text-center mb-8">
          <div className={`inline-block p-3 rounded-xl mb-4 ${darkMode ? 'bg-blue-900' : 'bg-blue-100'}`}>
            <Settings className={`w-10 h-10 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
          </div>
          <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Система управления</h1>
          <p className={`mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Вход в систему учета рабочего времени</p>
        </div>

        {loginError && (
          <div className={`mb-4 p-3 rounded-lg flex items-center space-x-2 ${darkMode ? 'bg-red-900/50 border border-red-800' : 'bg-red-50 text-red-600'}`}>
            <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
            <span className={darkMode ? 'text-red-300' : 'text-red-600'}>{loginError}</span>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Логин</label>
            <input
              type="text"
              value={username}
              onChange={(e) => { setUsername(e.target.value); setLoginError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              className={`w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
              }`}
              placeholder="Введите логин"
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Пароль</label>
            <input
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setLoginError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              className={`w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
              }`}
              placeholder="Введите пароль"
            />
          </div>
          <div className="flex justify-between items-center">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleLogin}
              className={`font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2 w-full sm:w-auto ${
                darkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              <LogIn className="w-5 h-5" />
              <span>Войти в систему</span>
            </motion.button>
            <button
              onClick={() => setShowForgotPassword(true)}
              className={`ml-4 text-sm font-medium ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
            >
              Забыли пароль?
            </button>
          </div>
          <div className="flex justify-center mt-4">
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                darkMode
                  ? 'bg-gray-700 text-yellow-300 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              <span className="text-sm">{darkMode ? "Светлая тема" : "Темная тема"}</span>
            </button>
          </div>
        </div>

        {showForgotPassword && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className={`mt-6 p-4 rounded-lg ${darkMode ? 'bg-blue-900/50 border border-blue-800' : 'bg-blue-50'}`}
          >
            <h3 className={`font-medium mb-2 ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>Восстановление пароля</h3>
            <p className={`mb-3 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
              Для восстановления пароля обратитесь к администратору системы.
            </p>
            <div className="space-y-3">
              <input
                type="text"
                value={resetRequest.username}
                onChange={(e) => setResetRequest({ ...resetRequest, username: e.target.value })}
                placeholder="Ваш логин"
                className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-blue-300'
                }`}
              />
              <input
                type="email"
                value={resetRequest.email}
                onChange={(e) => setResetRequest({ ...resetRequest, email: e.target.value })}
                placeholder="Ваш email"
                className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-blue-300'
                }`}
              />
              <button
                onClick={handlePasswordResetRequest}
                className={`w-full font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2 ${
                  darkMode ? 'bg-green-700 hover:bg-green-600 text-white' : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                <Mail className="w-4 h-4" />
                <span>Отправить запрос администратору</span>
              </button>
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};